// Vanilla loader served as `embed.js`. Auto-discovers `[data-eliza-embed]`
// containers, lazy-imports the appropriate widget chunk, and hands the
// container off for Shadow-DOM mounting. Kept dependency-free so partner
// pages get a tiny (~2KB gz) blocking script and only pay for the widget
// bundle once.
//
// Host-element attributes the loader reads (the rest of the surface is
// server config):
//   - `data-eliza-embed` (required) — the embed key.
//   - `data-eliza-minimised` (optional) — initial state only; the widget
//     watches it after mount (`lib/host-minimised.ts`).
//   - `data-eliza-demo`  (optional) — opt-in mode flag for playground
//     surfaces. Drives a small CSS + JS opt-in surface in the group
//     widget (pane-scoped positioning, set-page-context / set-theme /
//     session-start / tool-call demo events). NOT server-managed; this
//     is a static integration-mode switch, not branding config.
//
// Branding, greeting, and chat-enabled state come from the server-side
// config endpoint (`/chat/:embedKey/config`) so cockpit edits
// propagate without re-pasting the snippet.
//
// The regex below is intentionally identical to the server-side
// `embedKeySchema` in `apps/eliza/api/src/chat/schemas.ts`. Keeping the two
// in sync means a partner page with a stale legacy `ek_xxxxxxxxxx` snippet
// fails fast in the loader (visible `console.warn`) instead of mounting
// and then silently 400ing every config/stream call.

import { readMinimised } from './lib/host-minimised';
import { detectNoise, errorFields, initTelemetry, pushTelemetry } from './lib/telemetry';
import { captureAttribution } from './lib/utm';

const EMBED_KEY_RE = /^ek_grp_[a-z0-9]{10}$/;
const MOUNTED_ATTR = 'data-eliza-mounted';

// Self-heal (ELI-344): a stale `embed.js` — frozen for weeks by partner
// WordPress caches — carries a baked `import('./widget-<hash>.js')` whose file
// our deploys have since deleted, so the import dies. The live no-store
// manifest (emitted next to the loader by the widget build, served with
// `Cache-Control: no-store` — see `apps/eliza/api/src/handlers/widget-cache-headers.ts`)
// names the current chunk, so the visitor's browser can re-resolve it at
// failure time. Only `widget-<token>.js` siblings are loadable — the guard
// stops a poisoned/mangled manifest from turning into an arbitrary import.
const WIDGET_MANIFEST_FILE = 'widget-manifest.json';
const WIDGET_CHUNK_RE = /^widget-[a-zA-Z0-9_-]+\.js$/;

declare global {
  interface Window {
    __ELIZA_WIDGET_LOAD__?: Promise<typeof import('./widget')>;
  }
}

export function readEmbedKey(el: Element): string | null {
  const embedKey = el.getAttribute('data-eliza-embed');
  if (!embedKey || !EMBED_KEY_RE.test(embedKey)) return null;
  return embedKey;
}

/**
 * Opt-in demo flag. Reads `data-eliza-demo` on the host element. Any
 * value other than the literal string `"true"` (including missing,
 * empty, or `"false"`) resolves to `false` — production behaviour.
 *
 * When `true`, the group widget shell wires the foundOS-playground
 * document-event surface (`eliza:demo:set-page-context`,
 * `eliza:demo:set-theme`, `eliza:demo:session-start`,
 * `eliza:demo:tool` emission). Off by default so a stale partner
 * snippet can't accidentally enable these listeners.
 */
export function readIsDemo(el: Element): boolean {
  return el.getAttribute('data-eliza-demo') === 'true';
}

/** Strict `"true"` match — a structural flag the snippet author sets once (see AGENTS.md). */
export function readMarketplace(el: Element): boolean {
  return el.getAttribute('data-marketplace') === 'true';
}

// Inlined to avoid a router/value import in the loader (bundle hygiene); kept
// in lockstep with `requestedLocationTypeSchema` in
// `apps/eliza/api/src/chat/schemas.ts`.
const FORCED_LOCATION_TYPES = ['CARE_HOME', 'HOME_CARE'] as const;
type ForcedLocationType = (typeof FORCED_LOCATION_TYPES)[number];

/**
 * Optional forced chat location type. Reads `data-eliza-location-type` on the host
 * element; any value other than `CARE_HOME` / `HOME_CARE` (including missing or
 * empty) resolves to `undefined` — let the server derive from the roster. A
 * structural integration flag the snippet author sets once: currently only the
 * cockpit home-care demo, which shares one mixed-location-type workspace with the
 * care-home demo. The server honours it only when that location type is present in
 * the roster, so it can't force a location type the workspace doesn't have.
 */
export function readLocationType(el: Element): ForcedLocationType | undefined {
  const value = el.getAttribute('data-eliza-location-type');
  return FORCED_LOCATION_TYPES.find((t) => t === value);
}

export function getApiBase(
  currentScript: HTMLScriptElement | null = document.currentScript as HTMLScriptElement | null,
): string {
  // `import.meta.url` is the module-script equivalent of `currentScript.src`
  // (the latter is `null` under `type="module"`). Fall back to currentScript
  // for classic-script consumers, then to `window.location.origin`.
  try {
    if (typeof import.meta !== 'undefined' && import.meta.url) {
      const moduleUrl = new URL(import.meta.url);
      // Guard `file://` so jsdom (`file:///…/embed.ts`, origin = "null")
      // falls through to the currentScript / location.origin paths.
      if (moduleUrl.protocol === 'http:' || moduleUrl.protocol === 'https:') {
        return moduleUrl.origin;
      }
    }
  } catch {
    // fall through
  }
  try {
    const src = currentScript?.src;
    if (src) return new URL(src).origin;
  } catch {
    // fall through
  }
  return window.location.origin;
}

/**
 * URL of the loader itself (`embed.js`), used as the base for resolving the
 * sibling manifest and widget chunk. Mirrors `getApiBase`'s tiered resolution:
 * `import.meta.url` for module consumers, `currentScript.src` for classic, and
 * — where neither is an http(s) URL (jsdom, `file://`) — the known mount path
 * off the origin (kept in lockstep with the `/chat/widget/*` route in
 * `apps/eliza/api/src/index.ts`).
 */
export function getLoaderUrl(
  currentScript: HTMLScriptElement | null = document.currentScript as HTMLScriptElement | null,
): string {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.url) {
      const moduleUrl = new URL(import.meta.url);
      if (moduleUrl.protocol === 'http:' || moduleUrl.protocol === 'https:') {
        return moduleUrl.href;
      }
    }
  } catch {
    // fall through
  }
  try {
    const src = currentScript?.src;
    if (src) return new URL(src).href;
  } catch {
    // fall through
  }
  return `${getApiBase(currentScript)}/chat/widget/v1/embed.js`;
}

/**
 * A manifest lookup that failed. `answered` records whether our own origin
 * responded at all: a status code or a malformed body is positive evidence the
 * deploy is broken (actionable), whereas a rejected fetch is the visitor's
 * transport (not actionable). Carrying it as a field beats re-deriving it by
 * string-matching our own error messages downstream.
 */
export class ManifestError extends Error {
  readonly answered: boolean;

  constructor(message: string, answered: boolean, cause?: unknown) {
    super(message);
    this.name = 'ManifestError';
    this.answered = answered;
    if (cause instanceof Error && cause.stack) this.stack = cause.stack;
  }
}

/**
 * Fetch the no-store manifest and resolve the current widget chunk's absolute
 * URL, relative to the loader. Throws a `ManifestError` if the manifest is
 * unreachable, malformed, or names anything other than a `widget-<token>.js`
 * sibling.
 */
export async function resolveHealedChunkUrl(
  loaderUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const manifestUrl = new URL(WIDGET_MANIFEST_FILE, loaderUrl).href;
  let res: Response;
  try {
    res = await fetchImpl(manifestUrl, { cache: 'no-store' });
  } catch (err) {
    throw new ManifestError(`widget manifest unreachable: ${String(err)}`, false, err);
  }
  if (!res.ok) throw new ManifestError(`widget manifest fetch failed: ${res.status}`, true);
  let data: { widget?: unknown };
  try {
    data = (await res.json()) as { widget?: unknown };
  } catch (err) {
    throw new ManifestError('widget manifest body was not JSON', true, err);
  }
  if (typeof data.widget !== 'string' || !WIDGET_CHUNK_RE.test(data.widget)) {
    throw new ManifestError('widget manifest missing a valid widget field', true);
  }
  return new URL(data.widget, loaderUrl).href;
}

type WidgetModule = typeof import('./widget');

// Import seams, injectable for tests. The primary `import('./widget')` must stay
// a literal so Vite emits the widget chunk; the healed import takes the manifest
// URL and is `@vite-ignore`d so Vite leaves the computed specifier alone.
interface ChunkLoaders {
  importWidget?: () => Promise<WidgetModule>;
  importChunk?: (url: string) => Promise<WidgetModule>;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Base backoff (ms) waited before each chunk-import retry; its length is the
 * retry count, so total attempts = `+ 1` (3). Mirrors the `/config` retry in
 * `widget.tsx` — same shape, deliberately smaller caps because this one blocks
 * the bubble from painting at all.
 */
const CHUNK_RETRY_BACKOFFS_MS = [250, 700] as const;

/** Base delay + up to +50% random jitter, so retrying clients don't thunder. */
function chunkBackoff(baseMs: number): number {
  return baseMs + Math.random() * baseMs * 0.5;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Distinguishes a genuinely-broken deploy from the visitor's flaky network —
 * the whole point of SERVICE-ELIZA-3E/4N triage. `missing` means our own
 * origin answered "not there" (404/410, or a 4xx/5xx on the manifest), which is
 * actionable and must reach Sentry. `blip` means the request never landed, so
 * there is nothing for us to fix.
 */
export type ChunkFailureKind = 'missing' | 'blip';

/**
 * Probe the chunk URL after an import failure to decide which kind it was. A
 * non-ok response means the file is really gone; a rejected fetch means the
 * transport failed. An unreachable probe is treated as a blip — we only claim
 * "missing" on positive evidence from our own origin.
 */
export async function classifyChunkFailure(
  chunkUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ChunkFailureKind> {
  try {
    const res = await fetchImpl(chunkUrl, { method: 'GET', cache: 'no-store' });
    return res.ok ? 'blip' : 'missing';
  } catch {
    return 'blip';
  }
}

/** Thrown by `loadWidgetChunk` so the mount catch can classify without re-probing. */
export class ChunkLoadFailure extends Error {
  readonly kind: ChunkFailureKind;
  readonly chunkUrl: string | null;

  constructor(kind: ChunkFailureKind, chunkUrl: string | null, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'ChunkLoadFailure';
    this.kind = kind;
    this.chunkUrl = chunkUrl;
    if (cause instanceof Error && cause.stack) this.stack = cause.stack;
  }
}

/**
 * A retry specifier for `chunkUrl`. Re-importing the *same* specifier is a
 * guaranteed no-op: the ES module registry caches the failed module record, so
 * the second `import()` rejects from cache without touching the network. (This
 * is exactly why the original self-heal could never recover a blip — every 3E
 * event says `(self-heal failed)`.) A distinct query gets a fresh module record
 * and therefore a fresh request. Relative sub-imports inside the chunk still
 * resolve, because the directory part of the URL is unchanged.
 */
function retrySpecifier(chunkUrl: string, attempt: number): string {
  return `${chunkUrl}${chunkUrl.includes('?') ? '&' : '?'}elizaRetry=${attempt}`;
}

/**
 * Load the widget chunk, retrying transient failures and self-healing a stale
 * embed.
 *
 *  1. **Baked import.** The happy path — `import('./widget-<hash>.js')` as
 *     emitted by Vite, so a warm partner page pays nothing extra.
 *  2. **Resolve the live chunk.** On failure, read the no-store manifest. This
 *     both self-heals a stale embed (ELI-344 — a partner cache froze `embed.js`
 *     pointing at a hash a deploy deleted) and gives us a URL we can retry
 *     against explicitly.
 *  3. **Retry with backoff, on a cache-busted specifier.** The dominant failure
 *     is a transport blip against a chunk still being served (3E/4N: 593 events
 *     against a URL that returns 200). Retries are skipped while
 *     unloading/offline (`detectNoise`) — that is the visitor's environment.
 *  4. **Classify.** On final failure, probe the chunk: `missing` (our origin
 *     answered "gone" — a broken deploy, must reach Sentry) vs `blip` (the
 *     request never landed — logged for volume, never Sentry).
 *
 * Not memoised — `loadWidget` owns the once-per-page cache.
 */
export async function loadWidgetChunk(
  loaderUrl: string,
  {
    importWidget = () => import('./widget'),
    importChunk = (url) => import(/* @vite-ignore */ url) as Promise<WidgetModule>,
    fetchImpl = fetch,
    sleep = defaultSleep,
  }: ChunkLoaders = {},
): Promise<WidgetModule> {
  try {
    return await importWidget();
  } catch (bakedErr) {
    // biome-ignore lint/suspicious/noConsole: best-effort visibility for partner devs
    console.warn('[eliza-widget] widget chunk load failed; attempting self-heal', bakedErr);

    if (detectNoise() !== undefined) {
      // Unloading / offline: every further request is doomed, so don't stall the
      // visitor. Reported as a blip — their environment, not our deploy.
      throw new ChunkLoadFailure('blip', null, bakedErr);
    }

    let healedUrl: string;
    try {
      healedUrl = await resolveHealedChunkUrl(loaderUrl, fetchImpl);
    } catch (manifestErr) {
      // `manifestErr` is the cause rather than the baked import failure — a bad
      // manifest is the more specific diagnosis, and its status code is what
      // tells an on-call engineer what broke.
      const answered = manifestErr instanceof ManifestError && manifestErr.answered;
      throw new ChunkLoadFailure(answered ? 'missing' : 'blip', null, manifestErr);
    }

    let lastError: unknown = bakedErr;
    const maxAttempts = CHUNK_RETRY_BACKOFFS_MS.length + 1;

    for (let i = 0; i < maxAttempts; i++) {
      if (i > 0) await sleep(chunkBackoff(CHUNK_RETRY_BACKOFFS_MS[i - 1] ?? 0));
      try {
        // First attempt uses the manifest URL as-is: when the embed really was
        // stale this is a genuinely different chunk, and keeping it un-busted
        // lets the CDN serve it. Later attempts bust so they re-request.
        return await importChunk(i === 0 ? healedUrl : retrySpecifier(healedUrl, i));
      } catch (err) {
        lastError = err;
        if (detectNoise() !== undefined) break;
      }
    }

    throw new ChunkLoadFailure(
      await classifyChunkFailure(healedUrl, fetchImpl),
      healedUrl,
      lastError,
    );
  }
}

function loadWidget(loaderUrl: string): Promise<WidgetModule> {
  if (!window.__ELIZA_WIDGET_LOAD__) {
    window.__ELIZA_WIDGET_LOAD__ = loadWidgetChunk(loaderUrl);
  }
  return window.__ELIZA_WIDGET_LOAD__;
}

/**
 * Pin `content-visibility: visible` on the host.
 *
 * `content-visibility: auto` applies containment, making the host the
 * containing block for `position: fixed` descendants — through the shadow
 * boundary. The host is an empty, zero-height div at the end of `<body>`, so a
 * contained bubble resolves `bottom: 24px` against the foot of the document and
 * paints off-screen; a skipped subtree never animates either, so it also stays
 * at `opacity: 0`. Partner page-speed plugins do this to below-the-fold
 * elements wholesale — WP Rocket's Automatic Lazy Rendering emits
 * `[data-wpr-lazyrender] { content-visibility: auto }`.
 *
 * Inline beats any selector, so no `!important`. Covers this one property —
 * `contain`, `transform`, `filter` and `will-change` on an ancestor trap the
 * bubble the same way.
 */
export function unclipHost(el: Element): void {
  (el as HTMLElement).style?.setProperty('content-visibility', 'visible');
}

export function mountAll(): void {
  // First-touch attribution: runs on every page load (before the visitor
  // navigates off the landing URL), so a chat opened later on a deeper page
  // still carries the campaign that started the session.
  captureAttribution();
  const apiBase = getApiBase();
  const loaderUrl = getLoaderUrl();
  const containers = document.querySelectorAll('[data-eliza-embed]');
  for (const el of Array.from(containers)) {
    if (el.getAttribute(MOUNTED_ATTR) === '1') continue;
    const embedKey = readEmbedKey(el);
    if (!embedKey) {
      // Surface misconfiguration (typo, stale snippet) so partner devs
      // notice when the bubble silently fails to appear.
      const raw = el.getAttribute('data-eliza-embed');
      // biome-ignore lint/suspicious/noConsole: best-effort visibility for partner devs
      console.warn(
        `[eliza-widget] ignoring container with invalid data-eliza-embed value: ${JSON.stringify(raw)}`,
      );
      continue;
    }
    el.setAttribute(MOUNTED_ATTR, '1');
    unclipHost(el);
    const isDemo = readIsDemo(el);
    const locationType = readLocationType(el);
    const marketplace = readMarketplace(el);
    const minimised = readMinimised(el);
    // The widget passes the embedKey through to every `/chat/:embedKey/*`
    // call; the per-workspace mapping lives on the server.
    loadWidget(loaderUrl)
      .then(({ mount }) =>
        mount(el as HTMLElement, {
          embedKey,
          apiBase,
          isDemo,
          locationType,
          marketplace,
          minimised,
        }),
      )
      .catch((err) => {
        // Reached only when the retry loop and the self-heal both failed.
        // biome-ignore lint/suspicious/noConsole: load failure is best-effort visible
        console.error('[eliza-widget] failed to load widget chunk (self-heal failed)', err);
        el.removeAttribute(MOUNTED_ATTR);
        // Invisible server-side otherwise — the chunk never executes, so
        // nothing else can report it.
        initTelemetry({ apiBase, embedKey });
        // A `blip` is the visitor's transport against a chunk we are still
        // serving — logged for volume, never Sentry (SERVICE-ELIZA-3E/4N).
        // `missing` means our origin really lost the file: a broken deploy,
        // which must still raise. `detectNoise` covers unload/offline on top.
        const isBlip = err instanceof ChunkLoadFailure && err.kind === 'blip';
        pushTelemetry(
          {
            ...errorFields('widget chunk load failed (self-heal failed)', err),
            errorKind: 'chunk_load_failed',
            ...(isBlip && { noiseReason: 'chunk_fetch_blip' as const }),
          },
          { classifyNoise: true },
        );
      });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll);
} else {
  mountAll();
}
